const PROD_RIDES_URL = 'https://collectedaf-service-e5jme.ondigitalocean.app';
const API_URL = (process.env.REACT_APP_RIDES_API_URL || process.env.REACT_APP_API_URL || PROD_RIDES_URL).replace(/\/$/, '');

/** Format 24h time string (e.g. "14:00" or "14:00:00") to 12h with AM/PM */
export function formatTime(timeStr: string): string {
  if (!timeStr || !String(timeStr).trim()) return timeStr;
  const parts = String(timeStr).trim().split(':');
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10) || 0;
  if (Number.isNaN(h)) return timeStr;
  const hour = h % 12 || 12;
  const ampm = h < 12 ? 'AM' : 'PM';
  return `${hour}:${String(m).padStart(2, '0')} ${ampm}`;
}

export interface RideEventSlot {
  startTime: string;
  endTime: string;
  remaining: number;
  total: number;
  booked: number;
}

export interface PopinEvent {
  _id: string;
  name: string;
  displayTitle?: string;
  displaySubtitle?: string;
  bannerImage?: string;
  bannerImages?: { compact?: string; medium?: string };
  location?: { type: string; coordinates: number[] };
  eventMeta?: {
    isEvent?: boolean;
    eventType?: string;
    eventDate?: string;
    eventEndDate?: string;
  };
  interfaceType?: string;
  category?: string;
  emoji?: string;
  tags?: string[];
}

export interface RideEventSummary {
  _id: string;
  title: string;
  description?: string;
  status: string;
  schedule: {
    date: string;
    startTime: string;
    endTime: string;
    slotDurationMinutes: number;
  };
  pickup?: {
    address: string;
    coordinates: { lat: number; lng: number };
    instructions?: string;
    mapLink?: string;
  } | null;
  capacity: {
    totalRides: number;
    maxRidersPerSlot: number;
    maxPartySize: number;
  };
  display?: {
    heroImage?: string;
    promoText?: string;
    showRemainingSlots?: boolean;
  };
  transportMode: string;
  totalRidesRemaining: number;
  slotsAvailable: number;
  totalSlots: number;
  rideProgress?: RideProgress;
  event: PopinEvent | null;
}

export type RidePhase = 'booking' | 'in_progress' | 'completed';

export interface RideProgress {
  phase: RidePhase;
  total: number;
  inProgress: number;
  completed: number;
  pending: number;
  totalRiders: number;
}

export interface RideEventDetail {
  rideEvent: Omit<RideEventSummary, 'totalRidesRemaining' | 'slotsAvailable' | 'totalSlots' | 'event' | 'rideProgress'>;
  event: PopinEvent | null;
  slots: RideEventSlot[];
  totalRidesRemaining: number;
  rideProgress?: RideProgress;
}

export interface BookingResult {
  _id: string;
  status: string;
  slot: { date: string; startTime: string; endTime: string };
  accessToken: string;
}

async function parseJSON(res: Response): Promise<any> {
  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch {
    throw new Error(`Server returned non-JSON response (${res.status}). Check that REACT_APP_RIDES_API_URL points to the correct service.`);
  }
}

export async function fetchActiveEvents(): Promise<RideEventSummary[]> {
  const res = await fetch(`${API_URL}/api/v1/web-app/rides/events/active`);
  const data = await parseJSON(res);
  if (!res.ok) throw new Error(data.error || 'Failed to fetch events');
  return data.events || [];
}

export async function fetchEventDetail(eventId: string): Promise<RideEventDetail> {
  const res = await fetch(`${API_URL}/api/v1/web-app/rides/events/${eventId}`);
  const data = await parseJSON(res);
  if (!res.ok) throw new Error(data.error || 'Failed to fetch event details');
  return data;
}

export interface BookRideParams {
  rideEventId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  partySize: number;
  slotStartTime: string;
  slotEndTime: string;
  pickupAddress?: string;
  pickupLat?: number;
  pickupLng?: number;
}

export async function bookRide(params: BookRideParams): Promise<BookingResult> {
  const res = await fetch(`${API_URL}/api/v1/web-app/rides/book`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  const data = await parseJSON(res);
  if (!res.ok) throw new Error(data.error || data.errors?.[0]?.msg || 'Booking failed');
  return data.rideRequest;
}
